import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { DecisionTrailEntry } from "@ceo-cmd/shared";
import {
  getIngestBatch,
  getIngestStreamEvent,
  listIngestBatches,
  listIngestStreams,
  type IngestBatchSummary,
  type IngestStreamEventSummary,
} from "@/api/ingest";
import { listTodayDecisions } from "@/api/today";
import { getAccessToken } from "@/stores/authStore";
import {
  SettingsPageHeader,
  SettingsSection,
} from "@/components/settings/settings-ui";

type Tab = "batches" | "streams" | "decisions";

const SOURCES = [
  "",
  "apollo",
  "csv",
  "email",
  "slack",
  "external_dynamodb",
  "hubspot_webhook",
] as const;

async function downloadAuthed(url: string, filename: string) {
  const token = getAccessToken();
  const res = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    throw new Error(`Download failed (${res.status})`);
  }
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(objectUrl);
}

function DecisionRow({ row }: { row: DecisionTrailEntry }) {
  const kindLabel =
    row.kind === "disposition"
      ? row.disposition?.replace(/_/g, " ") ?? "disposition"
      : row.kind === "external_write"
        ? "external"
        : row.kind;

  const effectLabel = row.write
    ? row.write.simulated
      ? "simulated"
      : row.write.outcome === "skipped_disconnected"
        ? "not connected"
        : row.write.outcome
    : row.resultingStatus;

  const terminal =
    row.itemStatus === "COMPLETED" ||
    row.itemStatus === "DISMISSED" ||
    row.itemStatus === "SUPERSEDED";

  const openHref = (() => {
    const params = new URLSearchParams();
    params.set("item", row.itemId);
    if (!terminal && row.bucket) {
      params.set("bucket", row.bucket);
    } else {
      params.set("bucket", "all_active");
    }
    return `/?${params.toString()}`;
  })();

  return (
    <tr className="border-b border-border/60 text-sm align-top">
      <td className="py-2 pr-3 whitespace-nowrap text-muted-foreground">
        {new Date(row.at).toLocaleString()}
      </td>
      <td className="py-2 pr-3 capitalize">{kindLabel}</td>
      <td className="py-2 pr-3">
        <p className="font-medium text-foreground">
          {row.companyName ?? "—"}
        </p>
        <p className="text-xs text-muted-foreground line-clamp-2">
          {row.title ?? row.itemId}
        </p>
      </td>
      <td className="py-2 pr-3 text-muted-foreground max-w-md">
        <p className="line-clamp-3">{row.summary}</p>
        {effectLabel ? (
          <p className="mt-0.5 text-xs">
            {row.write?.system ? `${row.write.system}: ` : ""}
            {effectLabel}
          </p>
        ) : null}
      </td>
      <td className="py-2 whitespace-nowrap">
        {terminal ? (
          <span
            className="text-xs text-muted-foreground"
            title="Item left the active queue — expand Details on Today while it is still listed, or use the API events trail."
          >
            Closed
          </span>
        ) : (
          <Link to={openHref} className="text-brand underline text-xs">
            Open
          </Link>
        )}
      </td>
    </tr>
  );
}

function openUrl(url: string) {
  window.open(url, "_blank", "noopener,noreferrer");
}

function BatchRow({ batch }: { batch: IngestBatchSummary }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const loadDownloads = async () => {
    setBusy(true);
    setErr(null);
    try {
      const detail = await getIngestBatch(batch.batchId);
      return detail.downloads;
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to load downloads");
      return undefined;
    } finally {
      setBusy(false);
    }
  };

  return (
    <tr className="border-b border-border/60 text-sm">
      <td className="py-2 pr-3 whitespace-nowrap text-muted-foreground">
        {new Date(batch.createdAt).toLocaleString()}
      </td>
      <td className="py-2 pr-3 font-medium">{batch.source}</td>
      <td className="py-2 pr-3">{batch.connectionId}</td>
      <td className="py-2 pr-3">{batch.status}</td>
      <td className="py-2 pr-3 tabular-nums">
        {batch.counts.accepted}/{batch.counts.received}
        {batch.counts.rejected > 0 ? (
          <span className="text-amber-700"> · {batch.counts.rejected} rej</span>
        ) : null}
      </td>
      <td className="py-2 pr-3 font-mono text-xs">{batch.batchId}</td>
      <td className="py-2 space-x-2 whitespace-nowrap">
        {batch.s3Skipped ? (
          <span className="text-muted-foreground">S3 skipped</span>
        ) : (
          <>
            <button
              type="button"
              className="text-brand underline disabled:opacity-50"
              disabled={busy}
              onClick={() => {
                void loadDownloads().then((d) => {
                  if (d?.originalUrl) openUrl(d.originalUrl);
                });
              }}
            >
              Original
            </button>
            <button
              type="button"
              className="text-brand underline disabled:opacity-50"
              disabled={busy}
              onClick={() => {
                void loadDownloads().then((d) => {
                  if (d?.processedUrl) openUrl(d.processedUrl);
                });
              }}
            >
              Signals
            </button>
            <button
              type="button"
              className="text-brand underline disabled:opacity-50"
              disabled={busy}
              onClick={() => {
                void loadDownloads().then((d) => {
                  if (d?.exceptionsUrl) openUrl(d.exceptionsUrl);
                });
              }}
            >
              Exceptions
            </button>
            <button
              type="button"
              className="text-brand underline disabled:opacity-50"
              disabled={busy}
              onClick={() => {
                void (async () => {
                  setBusy(true);
                  setErr(null);
                  try {
                    const base = import.meta.env.VITE_API_BASE_URL ?? "";
                    await downloadAuthed(
                      `${base}/api/ingest/batches/${encodeURIComponent(batch.batchId)}/download`,
                      `${batch.batchId}.zip`,
                    );
                  } catch (e) {
                    setErr(e instanceof Error ? e.message : "Zip failed");
                  } finally {
                    setBusy(false);
                  }
                })();
              }}
            >
              Zip
            </button>
          </>
        )}
        {err ? <div className="text-destructive text-xs mt-1">{err}</div> : null}
      </td>
    </tr>
  );
}

function StreamRow({ event }: { event: IngestStreamEventSummary }) {
  return (
    <tr className="border-b border-border/60 text-sm">
      <td className="py-2 pr-3 whitespace-nowrap text-muted-foreground">
        {new Date(event.receivedAt).toLocaleString()}
      </td>
      <td className="py-2 pr-3 font-medium">{event.source}</td>
      <td className="py-2 pr-3">{event.connectionId}</td>
      <td className="py-2 pr-3">{event.processingStatus}</td>
      <td className="py-2 pr-3 font-mono text-xs">
        {event.batchId ? (
          <span title={event.batchId}>{event.batchId.slice(0, 24)}…</span>
        ) : (
          "—"
        )}
      </td>
      <td className="py-2 pr-3 font-mono text-xs">{event.streamEventId}</td>
      <td className="py-2">
        {event.s3Skipped ? (
          <span className="text-muted-foreground">S3 skipped</span>
        ) : (
          <button
            type="button"
            className="text-brand underline"
            onClick={() => {
              void getIngestStreamEvent(event.streamEventId).then((d) => {
                if (d.downloads?.eventUrl) openUrl(d.downloads.eventUrl);
              });
            }}
          >
            Download JSON
          </button>
        )}
      </td>
    </tr>
  );
}

export function SettingsObservabilityPage() {
  const [tab, setTab] = useState<Tab>("batches");
  const [source, setSource] = useState("");
  const [day, setDay] = useState(() => new Date().toISOString().slice(0, 10));
  const [hour, setHour] = useState(() =>
    String(new Date().getUTCHours()).padStart(2, "0"),
  );
  const [streamSource, setStreamSource] = useState("email");
  const [hourErr, setHourErr] = useState<string | null>(null);

  const batchesQuery = useQuery({
    queryKey: ["ingest-batches", source],
    queryFn: () =>
      listIngestBatches({
        source: source || undefined,
        limit: 50,
      }),
    staleTime: 10_000,
  });

  const streamsQuery = useQuery({
    queryKey: ["ingest-streams", streamSource, day, hour],
    queryFn: () =>
      listIngestStreams({
        source: streamSource || undefined,
        day,
        hour,
        limit: 100,
      }),
    enabled: tab === "streams",
    staleTime: 10_000,
  });

  const decisionsQuery = useQuery({
    queryKey: ["today-decisions"],
    queryFn: async () => {
      const res = await listTodayDecisions(80);
      return res.decisions;
    },
    enabled: tab === "decisions",
    staleTime: 10_000,
  });

  const sourceOptions = useMemo(
    () => SOURCES.map((s) => ({ value: s, label: s || "All sources" })),
    [],
  );

  return (
    <div className="space-y-6">
      <SettingsPageHeader
        title="Observability"
        description="Ingest warehouse, stream logs, and the CEO decision ledger — for reconciliation and honesty checks."
      />
      <p className="-mt-4 mb-4 text-sm text-muted-foreground">
        How this works:{" "}
        <Link to="/docs/observability" className="text-brand underline">
          Observability & warehouse guide
        </Link>
        {" · "}
        <Link to="/docs/operations" className="text-brand underline">
          Ops playbook
        </Link>
      </p>
      <div className="flex gap-2 border-b border-border pb-2">
        <button
          type="button"
          className={`px-3 py-1.5 text-sm rounded-md ${
            tab === "batches"
              ? "bg-brand text-brand-foreground"
              : "text-muted-foreground hover:bg-muted"
          }`}
          onClick={() => setTab("batches")}
        >
          Batches
        </button>
        <button
          type="button"
          className={`px-3 py-1.5 text-sm rounded-md ${
            tab === "streams"
              ? "bg-brand text-brand-foreground"
              : "text-muted-foreground hover:bg-muted"
          }`}
          onClick={() => setTab("streams")}
        >
          Stream log
        </button>
        <button
          type="button"
          className={`px-3 py-1.5 text-sm rounded-md ${
            tab === "decisions"
              ? "bg-brand text-brand-foreground"
              : "text-muted-foreground hover:bg-muted"
          }`}
          onClick={() => setTab("decisions")}
        >
          Decisions
        </button>
        <Link
          to="/settings/connections"
          className="ml-auto text-sm text-brand underline self-center"
        >
          Connections
        </Link>
      </div>

      {tab === "decisions" ? (
        <SettingsSection title="Decision trail">
          <div className="flex flex-wrap gap-3 mb-4 items-center">
            <p className="text-sm text-muted-foreground">
              Recent dispositions, external writes, undos, timers, and supersedes
              from the Command Item ledger.
            </p>
            <button
              type="button"
              className="text-sm text-brand underline"
              onClick={() => void decisionsQuery.refetch()}
            >
              Refresh
            </button>
          </div>
          {decisionsQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading decisions…</p>
          ) : decisionsQuery.error ? (
            <p className="text-sm text-destructive">
              {(decisionsQuery.error as Error).message}
            </p>
          ) : (decisionsQuery.data?.length ?? 0) === 0 ? (
            <p className="text-sm text-muted-foreground">
              No decision events yet. Act on an item from Today to populate this
              trail.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="text-xs uppercase tracking-wide text-muted-foreground border-b border-border">
                    <th className="py-2 pr-3">Time</th>
                    <th className="py-2 pr-3">Kind</th>
                    <th className="py-2 pr-3">Item</th>
                    <th className="py-2 pr-3">What happened</th>
                    <th className="py-2">Link</th>
                  </tr>
                </thead>
                <tbody>
                  {decisionsQuery.data!.map((row) => (
                    <DecisionRow key={`${row.eventId}-${row.kind}`} row={row} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </SettingsSection>
      ) : tab === "batches" ? (
        <SettingsSection title="Ingest batches">
          <div className="flex flex-wrap gap-3 mb-4 items-center">
            <label className="text-sm text-muted-foreground">
              Source{" "}
              <select
                className="ml-2 rounded-md border border-border bg-card px-2 py-1 text-sm"
                value={source}
                onChange={(e) => setSource(e.target.value)}
              >
                {sourceOptions.map((o) => (
                  <option key={o.value || "all"} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="text-sm text-brand underline"
              onClick={() => void batchesQuery.refetch()}
            >
              Refresh
            </button>
          </div>
          {batchesQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading batches…</p>
          ) : batchesQuery.error ? (
            <p className="text-sm text-destructive">
              {(batchesQuery.error as Error).message}
            </p>
          ) : (batchesQuery.data?.length ?? 0) === 0 ? (
            <p className="text-sm text-muted-foreground">
              No batches yet. Sync Apollo or upload CSV from Connections.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="text-xs uppercase tracking-wide text-muted-foreground border-b border-border">
                    <th className="py-2 pr-3">Time</th>
                    <th className="py-2 pr-3">Source</th>
                    <th className="py-2 pr-3">Connection</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2 pr-3">Accepted</th>
                    <th className="py-2 pr-3">Batch ID</th>
                    <th className="py-2">Downloads</th>
                  </tr>
                </thead>
                <tbody>
                  {batchesQuery.data!.map((b) => (
                    <BatchRow key={b.batchId} batch={b} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </SettingsSection>
      ) : (
        <SettingsSection title="Stream log">
          <p className="text-sm text-muted-foreground mb-4">
            Immutable push arrivals (email inject, HubSpot webhooks) keyed by UTC
            day/hour for reconciliation against batches and the signal lake.
          </p>
          <div className="flex flex-wrap gap-3 mb-4 items-end">
            <label className="text-sm text-muted-foreground">
              Source
              <select
                className="ml-2 block rounded-md border border-border bg-card px-2 py-1 text-sm text-foreground"
                value={streamSource}
                onChange={(e) => setStreamSource(e.target.value)}
              >
                <option value="email">email</option>
                <option value="hubspot_webhook">hubspot_webhook</option>
                <option value="">All</option>
              </select>
            </label>
            <label className="text-sm text-muted-foreground">
              Day (UTC)
              <input
                type="date"
                className="ml-2 block rounded-md border border-border bg-card px-2 py-1 text-sm text-foreground"
                value={day}
                onChange={(e) => setDay(e.target.value)}
              />
            </label>
            <label className="text-sm text-muted-foreground">
              Hour (UTC)
              <input
                type="number"
                min={0}
                max={23}
                className="ml-2 block w-20 rounded-md border border-border bg-card px-2 py-1 text-sm text-foreground"
                value={Number(hour)}
                onChange={(e) =>
                  setHour(String(Math.min(23, Math.max(0, Number(e.target.value)))).padStart(2, "0"))
                }
              />
            </label>
            <button
              type="button"
              className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted"
              onClick={() => {
                setHourErr(null);
                void (async () => {
                  try {
                    const base = import.meta.env.VITE_API_BASE_URL ?? "";
                    const sp = new URLSearchParams({ day, hour });
                    if (streamSource) sp.set("source", streamSource);
                    await downloadAuthed(
                      `${base}/api/ingest/streams/download?${sp}`,
                      `streams-${streamSource || "all"}-${day}-h${hour}.zip`,
                    );
                  } catch (e) {
                    setHourErr(
                      e instanceof Error ? e.message : "Hour download failed",
                    );
                  }
                })();
              }}
            >
              Download hour zip
            </button>
            <button
              type="button"
              className="text-sm text-brand underline"
              onClick={() => void streamsQuery.refetch()}
            >
              Refresh
            </button>
          </div>
          {hourErr ? (
            <p className="text-sm text-destructive mb-2">{hourErr}</p>
          ) : null}
          {streamsQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading stream events…</p>
          ) : streamsQuery.error ? (
            <p className="text-sm text-destructive">
              {(streamsQuery.error as Error).message}
            </p>
          ) : (streamsQuery.data?.length ?? 0) === 0 ? (
            <p className="text-sm text-muted-foreground">
              No stream events for this filter.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="text-xs uppercase tracking-wide text-muted-foreground border-b border-border">
                    <th className="py-2 pr-3">Received</th>
                    <th className="py-2 pr-3">Source</th>
                    <th className="py-2 pr-3">Connection</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2 pr-3">Batch</th>
                    <th className="py-2 pr-3">Event ID</th>
                    <th className="py-2">Download</th>
                  </tr>
                </thead>
                <tbody>
                  {streamsQuery.data!.map((e) => (
                    <StreamRow key={e.streamEventId} event={e} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </SettingsSection>
      )}
    </div>
  );
}
